import { useInfiniteFavorites } from "@/tanstack-hooks";
import Spinner from "./Spinner";
import EmptyState from "./EmptyState";
import { CloudOffIcon, Heart } from "lucide-react";
import ProfileCard from "./profile/ProfileCard";
import { FavoriteSortBy } from "@/generated-api";
import { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useLocation } from "react-router-dom";
import ScrollTopButton from "./ScrollTopButton";
import { useTranslation } from "@/hooks/useTranslation";
import PulseLoader from "./PulseLoader";

export default function FavoritesList({ sortBy }: { sortBy: FavoriteSortBy }) {
  const { ref, inView } = useInView({
    rootMargin: "400px 0px", // Starts observing 200px before the element comes into view
    threshold: 0,
  });
  const location = useLocation();
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteFavorites(sortBy);
  const [isInitialDelay, setIsInitialDelay] = useState(true);
  const fetchTimeoutRef = useRef<NodeJS.Timeout>();
  const pageRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const t = useTranslation();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialDelay(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleCardClick = () => {
    if (pageRef.current) {
      sessionStorage.setItem(location.pathname, window.scrollY.toString());
    }
  };

  useEffect(() => {
    const savedScroll = sessionStorage.getItem(location.pathname);
    if (savedScroll !== null) {
      const timer = setTimeout(() => {
        window.scrollTo(0, parseInt(savedScroll));
        sessionStorage.removeItem(location.pathname);
        setIsLoaded(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      // If no saved scroll, we can show content immediately
      setIsLoaded(true);
    }
  }, [data]);

  // Debounced fetch next page
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !isInitialDelay) {
      // Clear any existing timeout
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      // Set a new timeout for fetching the next page
      fetchTimeoutRef.current = setTimeout(() => {
        void fetchNextPage();
      }, 250);
    }

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage, isInitialDelay]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] w-full mx-auto">
        <PulseLoader />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <EmptyState
        icon={CloudOffIcon}
        title={t.FAVORITES_PAGE.ERRORS.LOADING}
        message={t.FAVORITES_PAGE.ERRORS.LOADING_DESCRIPTION}
      />
    );
  }

  const favorites = data.pages.flatMap((page) => page.items);

  return (
    <>
      {favorites.length === 0 ? (
        <EmptyState
          icon={Heart}
          title={t.FAVORITES_PAGE.LABELS.MY_FAVORITES_EMPTY}
          message={t.FAVORITES_PAGE.LABELS.MY_FAVORITES_EMPTY_DESCRIPTION}
        />
      ) : (
        <div
          className={`flex flex-col w-full mt-2 ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          ref={pageRef}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {favorites.map((profile) => (
              <ProfileCard
                profile={profile}
                key={profile.user_id}
                onClick={handleCardClick}
              />
            ))}
          </div>

          <div
            ref={ref}
            className="w-full h-20 flex items-center justify-center"
          >
            {isFetchingNextPage && <Spinner />}
          </div>
          <ScrollTopButton />
        </div>
      )}
    </>
  );
}
